import {
  CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import { ProductCarouselSlide } from 'shared-types'
import { Product } from '~/types'
import { Gallery } from './Gallery'
import { hasVisibleThumbnails } from './GalleryThumbnails'
import styles from './ProductDashboardGallery.module.scss'
import { ProductDashboardGalleryModal } from './ProductDashboardGalleryModal'

export interface ProductDashboardGalleryProps {
  product: Product
  rawWebContent?: string
}

// Never collapse the gallery past this, however little room is left below it.
const MIN_GALLERY_HEIGHT = 360
// Breathing room between the bottom of the gallery and the fold.
const VIEWPORT_BOTTOM_GAP = 16

// Measure before the browser paints, so the gallery is revealed at its final
// size rather than being painted oversized and corrected a frame later. React
// warns about useLayoutEffect during SSR, where there is nothing to measure.
const useIsomorphicLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

export const ProductDashboardGallery = ({
  product,
  rawWebContent,
}: ProductDashboardGalleryProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [maxHeight, setMaxHeight] = useState<number>()
  const rootRef = useRef<HTMLDivElement>(null)

  useIsomorphicLayoutEffect(() => {
    const element = rootRef.current

    if (!element) {
      return undefined
    }

    const measure = () => {
      // Where the gallery starts in the document, which is also its distance
      // from the top of the viewport with the page scrolled to the top.
      const offsetTop = element.getBoundingClientRect().top + window.scrollY
      const available = window.innerHeight - offsetTop - VIEWPORT_BOTTOM_GAP
      const next = Math.round(Math.max(MIN_GALLERY_HEIGHT, available))

      setMaxHeight((previous) => {
        return previous === next ? previous : next
      })
    }

    measure()

    // Content above the gallery (highlights, banners, fonts) settles after the
    // first paint and moves the gallery's starting point with it.
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(document.body)
    window.addEventListener('resize', measure)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  const gallerySlides = useMemo<ProductCarouselSlide[]>(() => {
    const slides = (product?.carousel?.slides ?? []).filter((slide) => {
      return slide.type === 'image' && !!slide.src
    })

    if (rawWebContent) {
      slides.push({
        type: 'video',
        src: rawWebContent,
        height: 0,
        width: 0,
        altText: '',
      })
    }

    return slides
  }, [product?.carousel?.slides, rawWebContent])

  if (gallerySlides.length === 0) {
    return null
  }

  // Mirrors the Gallery's own check, so the root only reserves a row for the
  // thumbnail strip under the media when the strip is actually rendered.
  const showThumbnails = hasVisibleThumbnails(gallerySlides, false)

  return (
    <>
      <div
        ref={rootRef}
        className={classNames(styles.root, {
          [styles.withThumbnails]: showThumbnails,
          // Hidden until the first measurement lands, otherwise the gallery is
          // painted at its unclamped height until hydration runs and snaps it.
          [styles.measuring]: maxHeight === undefined,
        })}
        style={
          {
            '--pdp-gallery-max-height': maxHeight
              ? `${maxHeight}px`
              : undefined,
          } as CSSProperties
        }>
        <Gallery
          slides={gallerySlides}
          selectedIndex={selectedIndex}
          onSelect={setSelectedIndex}
          onImageClick={() => {
            setIsModalOpen(true)
          }}
          preloadImages
          thumbnailsBelowMedia
        />
      </div>
      <ProductDashboardGalleryModal
        slides={gallerySlides}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
        }}
      />
    </>
  )
}
